import React, { useState, useMemo } from "react";
import { Users, Split, Copy, Share2, Link, UserPlus, X, Check, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency, FUEL_TYPES } from "./constants";

export default function SplitShare({ liveResult, distance, consumption, selectedFuelType, currentFuelPrice, returnTrip, originInput, destinationInput, tripName }) {
  const [splitCount, setSplitCount] = useState(2);
  const [splitMode, setSplitMode] = useState("equal"); // "equal" or "custom"
  const [customNames, setCustomNames] = useState(["Driver", "Passenger"]);
  const [customAmounts, setCustomAmounts] = useState([]);
  const [showSection, setShowSection] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  if (!liveResult) return null;

  const perPerson = Math.round((liveResult.total_cost / splitCount) * 100) / 100;
  const remainder = Math.round((liveResult.total_cost - perPerson * splitCount) * 100) / 100;

  const adjustSplitCount = (delta) => {
    const newCount = Math.max(2, Math.min(10, splitCount + delta));
    setSplitCount(newCount);
    // Sync custom names
    const names = [...customNames];
    while (names.length < newCount) names.push(`Person ${names.length + 1}`);
    setCustomNames(names.slice(0, newCount));
    setCustomAmounts([]);
  };

  const updateCustomName = (idx, name) => {
    const names = [...customNames];
    names[idx] = name;
    setCustomNames(names);
  };

  const updateCustomAmount = (idx, value) => {
    const amounts = customAmounts.length === splitCount ? [...customAmounts] : Array(splitCount).fill(perPerson);
    amounts[idx] = parseFloat(value) || 0;
    setCustomAmounts(amounts);
  };

  const customTotal = customAmounts.length === splitCount ? customAmounts.reduce((s, a) => s + a, 0) : 0;
  const customDiff = customAmounts.length === splitCount ? Math.round((liveResult.total_cost - customTotal) * 100) / 100 : 0;

  // Build share text
  const buildShareText = () => {
    const lines = [];
    const label = tripName || (originInput && destinationInput ? `${originInput.split(",")[0]} to ${destinationInput.split(",")[0]}` : "Trip");
    lines.push(`DRIVE$PEND - ${label}`);
    lines.push(`Total: ${formatCurrency(liveResult.total_cost)} (${distance} km${returnTrip ? " return" : ""})`);
    lines.push(`${FUEL_TYPES[selectedFuelType]?.label || "Fuel"} @ ${formatCurrency(currentFuelPrice)}/L`);
    if (liveResult.ruc_cost > 0) lines.push(`Includes RUC: ${formatCurrency(liveResult.ruc_cost)}`);
    if (liveResult.toll_cost > 0) lines.push(`Includes Tolls: ${formatCurrency(liveResult.toll_cost)}`);
    if (liveResult.ferry_cost > 0) lines.push(`Includes Ferry: ${formatCurrency(liveResult.ferry_cost)}`);
    lines.push("");
    lines.push(`Split ${splitCount} ways:`);
    if (splitMode === "equal") {
      lines.push(`${formatCurrency(perPerson)} each`);
    } else if (customAmounts.length === splitCount) {
      customNames.slice(0, splitCount).forEach((name, i) => {
        lines.push(`  ${name}: ${formatCurrency(customAmounts[i] || 0)}`);
      });
    }
    return lines.join("\n");
  };

  const handleCopyText = async () => {
    const text = buildShareText();
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Cost breakdown copied!");
    } catch {
      const input = document.createElement("textarea");
      input.value = text; document.body.appendChild(input); input.select();
      document.execCommand("copy"); document.body.removeChild(input);
      toast.success("Copied!");
    }
  };

  const handleShareNative = async () => {
    const text = buildShareText();
    if (navigator.share) {
      try { await navigator.share({ title: "DRIVE$PEND Trip Cost", text }); } catch (err) { /* user cancelled share */ }
    } else {
      handleCopyText();
    }
  };

  const handleCopyLink = () => {
    const params = new URLSearchParams();
    params.set("share", "1");
    if (distance) params.set("d", distance);
    if (consumption) params.set("c", consumption);
    params.set("ft", selectedFuelType);
    if (currentFuelPrice) params.set("p", currentFuelPrice.toString());
    if (returnTrip) params.set("rt", "1");
    if (tripName) params.set("tn", tripName);
    if (originInput) params.set("or", originInput);
    if (destinationInput) params.set("de", destinationInput);
    params.set("sp", splitCount.toString());
    const url = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true); toast.success("Share link with split copied!");
      setTimeout(() => setLinkCopied(false), 2000);
    }).catch(() => {
      const ta = document.createElement("textarea");
      ta.value = url; document.body.appendChild(ta); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta);
      setLinkCopied(true); toast.success("Share link copied!");
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  return (
    <div className="sf-split-section" data-testid="split-share-section">
      <button
        onClick={() => setShowSection(!showSection)}
        className="w-full flex items-center justify-between py-3 px-1 group"
        data-testid="split-share-toggle"
      >
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-[#E52020]" />
          <span className="text-xs font-bold text-[#666666] tracking-wider font-['Manrope']">SPLIT & SHARE</span>
        </div>
        <div className="flex items-center gap-2">
          {showSection && <span className="text-sm font-mono text-[#E52020] font-bold">{formatCurrency(perPerson)}/person</span>}
          <span className={`text-[#999999] transition-transform duration-200 ${showSection ? "rotate-180" : ""}`}>
            <Minus className={`w-4 h-4 ${showSection ? "block" : "hidden"}`} />
            <Plus className={`w-4 h-4 ${showSection ? "hidden" : "block"}`} />
          </span>
        </div>
      </button>

      {showSection && (
        <div className="pt-2 pb-1 space-y-4" data-testid="split-share-content">
          {/* People count */}
          <div className="flex items-center justify-between">
            <span className="text-xs font-bold text-[#666666] tracking-wider font-['Manrope']">PEOPLE</span>
            <div className="flex items-center gap-1">
              <button onClick={() => adjustSplitCount(-1)} disabled={splitCount <= 2}
                className="w-8 h-8 rounded-full border border-[#EBE6DC] flex items-center justify-center text-[#666666] hover:border-[#E52020] hover:text-[#E52020] transition-colors disabled:opacity-30"
                data-testid="split-decrease">
                <Minus className="w-3.5 h-3.5" />
              </button>
              <span className="w-10 text-center text-lg font-bold font-mono text-[#1A1A1A]" data-testid="split-count">{splitCount}</span>
              <button onClick={() => adjustSplitCount(1)} disabled={splitCount >= 10}
                className="w-8 h-8 rounded-full border border-[#EBE6DC] flex items-center justify-center text-[#666666] hover:border-[#E52020] hover:text-[#E52020] transition-colors disabled:opacity-30"
                data-testid="split-increase">
                <Plus className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Split mode toggle */}
          <div className="flex gap-1 bg-[#F7F5F0] rounded-sm p-0.5">
            <button onClick={() => { setSplitMode("equal"); setCustomAmounts([]); }}
              className={`flex-1 py-1.5 text-xs font-['Manrope'] font-bold rounded-sm transition-all ${splitMode === "equal" ? "bg-white text-[#E52020] shadow-sm" : "text-[#999999]"}`}
              data-testid="split-mode-equal">
              EQUAL SPLIT
            </button>
            <button onClick={() => { setSplitMode("custom"); setCustomAmounts(Array(splitCount).fill(perPerson)); }}
              className={`flex-1 py-1.5 text-xs font-['Manrope'] font-bold rounded-sm transition-all ${splitMode === "custom" ? "bg-white text-[#E52020] shadow-sm" : "text-[#999999]"}`}
              data-testid="split-mode-custom">
              CUSTOM
            </button>
          </div>

          {/* Equal split display */}
          {splitMode === "equal" && (
            <div className="text-center py-3" data-testid="equal-split-display">
              <div className="text-2xl font-bold font-mono text-[#E52020]">{formatCurrency(perPerson)}</div>
              <div className="text-xs text-[#666666] font-['Manrope'] mt-1">per person ({splitCount} people)</div>
              {remainder !== 0 && <div className="text-[10px] text-[#999999] mt-1">Rounding: {formatCurrency(Math.abs(remainder))} {remainder > 0 ? "remaining" : "over"}</div>}
            </div>
          )}

          {/* Custom split */}
          {splitMode === "custom" && (
            <div className="space-y-2" data-testid="custom-split-display">
              {Array.from({ length: splitCount }).map((_, i) => (
                <div key={`split-row-${i}-${splitCount}`} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={customNames[i] || `Person ${i + 1}`}
                    onChange={(e) => updateCustomName(i, e.target.value)}
                    className="flex-1 text-xs font-['Manrope'] bg-[#FAFAF7] border border-[#EBE6DC] rounded-sm px-2 py-1.5 text-[#1A1A1A] outline-none focus:border-[#E52020]"
                    data-testid={`split-name-${i}`}
                  />
                  <div className="flex items-center bg-[#FAFAF7] border border-[#EBE6DC] rounded-sm px-2 py-1.5">
                    <span className="text-xs text-[#666666]">$</span>
                    <input
                      type="number"
                      value={customAmounts[i] !== undefined ? customAmounts[i] : perPerson}
                      onChange={(e) => updateCustomAmount(i, e.target.value)}
                      className="w-16 text-xs font-mono bg-transparent outline-none text-[#1A1A1A] pl-1"
                      step="0.01" min="0"
                      data-testid={`split-amount-${i}`}
                    />
                  </div>
                </div>
              ))}
              {customAmounts.length === splitCount && customDiff !== 0 && (
                <div className={`text-[10px] text-center font-mono ${Math.abs(customDiff) < 0.05 ? "text-[#16A34A]" : "text-[#DC2626]"}`}>
                  {customDiff > 0 ? `${formatCurrency(customDiff)} unallocated` : `${formatCurrency(Math.abs(customDiff))} over-allocated`}
                </div>
              )}
            </div>
          )}

          {/* Share buttons */}
          <div className="flex gap-2 pt-1">
            <button onClick={handleCopyText}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-['Manrope'] font-bold text-[#E52020] bg-[rgba(229, 32, 32,0.06)] border border-[rgba(229, 32, 32,0.15)] rounded-sm hover:bg-[rgba(229, 32, 32,0.12)] transition-colors"
              data-testid="split-copy-text">
              <Copy className="w-3.5 h-3.5" />COPY
            </button>
            <button onClick={handleShareNative}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 text-xs font-['Manrope'] font-bold text-white bg-[#E52020] rounded-sm hover:bg-[#CC1B1B] transition-colors"
              data-testid="split-share-btn">
              <Share2 className="w-3.5 h-3.5" />SHARE
            </button>
            <button onClick={handleCopyLink}
              className="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-['Manrope'] font-bold text-[#666666] bg-[#FAFAF7] border border-[#EBE6DC] rounded-sm hover:border-[#E52020] hover:text-[#E52020] transition-colors"
              data-testid="split-copy-link">
              {linkCopied ? <Check className="w-3.5 h-3.5 text-[#16A34A]" /> : <Link className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
